import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import useNavigate from '@hook/useNavigate'
import { useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { useAccessUser } from '@/hook/useAccessUser'
import { useDisabledHover } from '@/hook/useDisabledHover'
import {
    DOC_NOT_READY_STATUSES,
    MAIN_TAB,
    RUNSHEET_HEADERS,
    TAB_TYPE_DEED,
} from '@/constants'
import { useRealtimeRunsheetLock } from '@/hook/useRealtimeRunsheetLock'
import { useRealtimeDocumentsStatus } from '@/hook/useRealtimeDocumentsStatus'
import useAbortableFetch from '@/hook/useAbortableFetch'
import { usePollDocumentsStatus } from '@/hook/usePollDocumentsStatus'
import { usePollRunsheetLock } from '@/hook/usePollRunsheetLock'
import { getRunsheetDocsByTab } from './Runsheet.lib'
import { toast } from 'react-toastify'
import useGlobalStateQuery from '@hook/useGlobalStateQuery.js'

const log = new Logger('RunsheetViewModel')

const RunsheetViewModel = () => {
    
    const uploadRef = useRef()
    const customizationRef = useRef()
    
    const { id, tractId } = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [markedDocuments, setMarkedDocuments] = useState([])
    const [editRsDocJoin, setEditRsDocJoin] = useState()
    const [showAdd, setShowAdd] = useState(false)
    const [showPlot, setShowPlot] = useState(false)
    const [showAssignTract, setShowAssignTract] = useState(false)
    const [showCreateTract, setShowCreateTract] = useState(false)
    const [showAddCollection, setShowAddCollection] = useState(false)
    const [showEdit, setShowEdit] = useState(false)
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
    const [showProcessModal, setShowProcessModal] = useState(false)
    const [showBulkDeleteDocs, setShowBulkDeleteDocs] = useState(false)
    const [showAddToTabModal, setShowAddToTabModal] = useState(false)
    const [updateDocs, setUpdateDocs] = useState(false)
    const [copyRunsheetModal, setCopyRunsheetModal] = useState(false)
    const [copyDocumentsModal, setCopyDocumentsModal] = useState(false)
    const [createRunsheetDialogOpen, setCreateRunsheetDialogOpen] = useState(false)
    const [, setRunsheetDocuments] = useState([])
    const [copyTract, setCopyTract] = useState(false)
    const [copyChaining, setCopyChaining] = useState(false)
    const [itemsToCopy, setItemsToCopy] = useState({})
    const [newRunsheetName, setNewRunsheetName] = useState('')
    const [uploadedFiles, setUploadedFiles] = useState()
    const [collectionId, setCollectionId] = useState(null)
    const [showCategorizationModal, setShowCategorizationModal] = useState(false)
    
    const [currentRunsheet, setCurrentRunsheet] = useWireState(store.currentRunsheet)
    const tabs = useWireValue(store.runsheetTabs)
    const documents = useWireValue(store.documents)
    const [exportRunsheetDialogOpen, setExportRunsheetDialogOpen] = useWireState(store.exportRunsheetDialogOpen)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const activeTab = useMemo(() => searchParams.get('activeTab') || MAIN_TAB, [searchParams])
    const currentTab = useMemo(() => tabs.find(t => t.id === activeTab) || tabs[0], [tabs, activeTab])
    const runsheetDocs = useMemo(() => getRunsheetDocsByTab(currentTab, currentRunsheet), [currentRunsheet, currentTab])
    const [ownershipType, setOwnershipType] = useState(activeTab.type || TAB_TYPE_DEED)
    
    const isCategorizationEnabled = useMemo(() => remoteConfig.chainer?.leaseCategorizationEnabled, [remoteConfig])
    
    const runsheetDocsLockedIds = useMemo(() => (
        runsheetDocs?.filter(it => it.locked)?.map(it => it.id) || []
    ), [runsheetDocs])
    
    // if important data is missing, then inform the user through a tooltip
    const missingForChainer = useMemo(() => runsheetDocs.some(r =>
        !r.value.instrumentDate || !r.value.grantors.length || !r.value.grantees.length)
    , [runsheetDocs])
    
    const tract = useMemo(() => {
        const tracts = currentRunsheet?.tracts
        
        if (tracts) {
            if (!currentTab || currentTab.id === MAIN_TAB) {
                return tracts.find(t => t.joins.some(j => !j.runsheetTabId)) || {}
            } else {
                return tracts.find(t => t.joins.some(j => j.runsheetTabId === currentTab.id)) || {}
            }
        }
        
        return null
    }, [currentRunsheet, currentTab])
    
    const documentToRunsheetAccess = useAccessUser({
        accessType: 'specific_collection',
        accessName: 'add_documents_to_runsheet',
    })
    const changeRunsheetAccess = useAccessUser({
        accessType: 'additional',
        accessName: 'change_tract_runsheet_settings',
    })
    const exportRunsheetAccess = useAccessUser({
        accessType: 'additional',
        accessName: 'export_runsheet_from_a_specific_runsheet_page',
    })
    
    const documentTo = useDisabledHover(!documentToRunsheetAccess)
    const changeRunsheet = useDisabledHover(!changeRunsheetAccess)
    const exportRunsheet = useDisabledHover(!exportRunsheetAccess)
    
    const fetchRunsheetById = async id => {
        
        try {
            
            await actions.fetchRunsheetById(id)
            await actions.getTabsFromRunsheet(id)
            await actions.getTractsFromRunsheet(id)
            await actions.getChainsFromRunsheet(id)
            
            // getting the latest value because the wireState might not be updated yet
            return store.currentRunsheet.getValue()
            
        } catch (e) {
            
            console.error('Error fetching runsheet', e?.request?.status, '==')
            
            if (e?.request?.status === 403)
                setError('You do not have permission to view this runsheet.')
            else
                setError('An error occurred while loading this runsheet.')
            throw e
            
        }
        
    }
    
    const handleExportRunsheetClick = () => {
        // setExportableDocuments(markedDocuments.length ? markedDocuments : runsheet.rdj.map(r => r.document.id))
        setExportRunsheetDialogOpen(!exportRunsheetDialogOpen)
    }
    
    const handleUpdateDocuments = useCallback(async () => {
        if (!currentRunsheet?.id) return
        const docsToEnhance = markedDocuments
            .map(docId => runsheetDocs.find(r => r.document.id === docId))
            .map(r => (r.document.id))
        
        await Promise.all(docsToEnhance.map(docId => actions
            .handleRunsheetAnnotationProcess(currentRunsheet.id, docId)))
        await fetchRunsheetById(currentRunsheet.id)
        setMarkedDocuments([])
    }, [markedDocuments, runsheetDocs, currentRunsheet])
    
    const onDrop = async files => {
        
        try {
            const validFiles = files.filter(it => it.name?.length)
            
            if (validFiles.length < 1) return
            
            setShowAddCollection(true)
            setUploadedFiles(validFiles)
        } catch (e) {
            console.error('onDrop failed', e)
        }
    }
    
    const removeDocumentsBulk = async () => {
        if (currentRunsheet?.id?.length && markedDocuments.length) {
            await actions.deleteDocumentFromRunsheet(currentRunsheet.id, markedDocuments)
        } else {
            return log.e('Runsheet ID or documentIds missing', { runsheetId: id, documentIds: markedDocuments })
        }
        
        // Re-fetch documents in this runsheet
        await fetchRunsheetById(currentRunsheet.id)
        toast.success(`Removed ${markedDocuments.length} ` +
            `${markedDocuments.length === 1 ? 'document' : 'documents'} successfully`)
        setMarkedDocuments([])
    }
    
    const removeDocsFromTab = async () => {
        if (!markedDocuments) return
        
        try {
            await actions.removeDocumentsFromTab(id, activeTab, markedDocuments)
            setMarkedDocuments([])
            toast.success('Successfully removed documents from tab.')
        } catch (err) {
            console.log(err)
            toast.error('Failed to remove documents from tab.')
        }
    }
    
    const submitCopyRunsheet = async () => {
        
        setLoading(true)
        setCopyRunsheetModal(false)
        const returnedCopy = await actions.copyRunsheet(id, newRunsheetName, itemsToCopy)
        
        navigate(`/runsheet/${returnedCopy.id}`)
        setLoading(false)
        
    }
    
    const submitCopyDocuments = async (toRunsheetId, documentsToCopy) => {
        
        await actions.copyDocumentsToRunsheet(id, toRunsheetId, documentsToCopy)
        
        navigate(`/runsheet/${toRunsheetId}`)
        
    }
    
    useRealtimeRunsheetLock(runsheetDocsLockedIds)
    usePollRunsheetLock(runsheetDocsLockedIds)
    
    useRealtimeDocumentsStatus('runsheet')
    usePollDocumentsStatus(runsheetDocs
        ?.filter(it => DOC_NOT_READY_STATUSES.includes(it.document.reviewStatus))
        ?.map(it => ({ id: it.document.id, reviewStatus: it.document.reviewStatus })) || [])
    
    /* useEffect(() => {
        setLoading(true)
        
        actions.fetchRunsheets()
            .then(() => {
                setLoading(false)
            })
        
    }, []) */
    
    useEffect(() => {
        setMarkedDocuments(prevMarkedDocs => {
            if (prevMarkedDocs.length === 0) return prevMarkedDocs
            
            return prevMarkedDocs.filter(docId => runsheetDocs.some(rd => rd.documentId === docId))
        })
    }, [runsheetDocs])
    
    useEffect(() => {
        setItemsToCopy(
            {
                chaining: copyChaining,
                tracts: copyTract,
            },
        )
    }, [copyTract, copyChaining])
    
    useEffect(() => {
        
        if (!id) {
            navigate.toRunsheets()
            return
        }
        
        setCurrentRunsheet({})
        
    }, [id])
    
    useEffect(() => {
        
        if (currentRunsheet)
            setRunsheetDocuments(currentRunsheet.rdj ?? [])
        
    }, [currentRunsheet])
    
    useEffect(() => {
        if (!documents?.rows?.length || !runsheetDocs?.length) return
        
        const hasDiffStatus = runsheetDocs.some(r => {
            const foundDoc = documents.rows.find(doc => doc.id === r.document.id)
            
            if (!foundDoc) return false
            
            return (foundDoc.reviewStatus !== r.document.reviewStatus
                || foundDoc.orgStatus !== r.document.orgStatus)
        })
        
        if (hasDiffStatus) {
            const copy = { ...currentRunsheet }
            
            copy.rdj = [...runsheetDocs]
            
            copy.rdj.forEach((r, idx) => {
                const foundDoc = documents.rows.find(doc => doc.id === r.document.id)
                
                if (foundDoc && (foundDoc.reviewStatus !== r.document.reviewStatus
                    || foundDoc.orgStatus !== r.document.orgStatus)
                ) {
                    const copyValue = { ...copy.rdj[idx] }
                    
                    copyValue.document = ({
                        ...r.document,
                        reviewStatus: foundDoc.reviewStatus,
                        orgStatus: foundDoc.orgStatus,
                    })
                    copy.rdj[idx] = copyValue
                }
            })
            
            setCurrentRunsheet(copy)
        }
    }, [runsheetDocs, documents])
    
    /* useEffect(() => {
        if (updateDocs)
            fetchRunsheetById(id)
    }, [id, updateDocs]) */
    
    useAbortableFetch(() => {
        if (currentRunsheet?.partyCasing)
            return actions.updateRunsheet(id, { partyCasing: currentRunsheet.partyCasing })
    }, [currentRunsheet?.partyCasing])
    
    const {
        // data: runsheet,
        error: runsheetError,
        isLoading: runsheetIsLoading,
        refetch: fetchRunsheet,
    } = useGlobalStateQuery(store.currentRunsheet, {
        queryKey: ['currentRunsheet', id, updateDocs],
        queryFn: async () => {
            try {
                return await fetchRunsheetById(id)
            } catch (e) {
                console.error('fetchRunsheetById', e)
                toast.error('Failed to fetch runsheet')
                throw e
            }
        },
        retry: 0,
    })
    
    return {
        
        // Constants
        RUNSHEET_HEADERS,
        
        // Refs
        uploadRef,
        customizationRef,
        
        // State
        error,
        setError,
        loading,
        setLoading,
        markedDocuments,
        setMarkedDocuments,
        editRsDocJoin,
        setEditRsDocJoin,
        showAdd,
        setShowAdd,
        showPlot,
        setShowPlot,
        showAssignTract,
        setShowAssignTract,
        showCreateTract,
        setShowCreateTract,
        showAddCollection,
        setShowAddCollection,
        showEdit,
        setShowEdit,
        uploadDialogOpen,
        setUploadDialogOpen,
        showProcessModal,
        setShowProcessModal,
        showBulkDeleteDocs,
        setShowBulkDeleteDocs,
        showAddToTabModal,
        setShowAddToTabModal,
        updateDocs,
        setUpdateDocs,
        copyRunsheetModal,
        setCopyRunsheetModal,
        copyDocumentsModal,
        setCopyDocumentsModal,
        createRunsheetDialogOpen,
        setCreateRunsheetDialogOpen,
        // ?? @todo
        setRunsheetDocuments,
        copyTract,
        setCopyTract,
        copyChaining,
        setCopyChaining,
        itemsToCopy,
        setItemsToCopy,
        newRunsheetName,
        setNewRunsheetName,
        uploadedFiles,
        setUploadedFiles,
        collectionId,
        setCollectionId,
        showCategorizationModal,
        setShowCategorizationModal,
        ownershipType,
        setOwnershipType,
        
        // Global State
        currentRunsheet,
        setCurrentRunsheet,
        tabs,
        documents,
        exportRunsheetDialogOpen,
        setExportRunsheetDialogOpen,
        
        // Memos
        missingForChainer,
        activeTab,
        currentTab,
        runsheetDocs,
        runsheetDocsLockedIds,
        tract,
        
        // Queries
        runsheetError,
        runsheetIsLoading,
        fetchRunsheet,
        
        // Hooks
        id,
        tractId,
        searchParams,
        navigate,
        documentToRunsheetAccess,
        changeRunsheetAccess,
        exportRunsheetAccess,
        documentTo,
        changeRunsheet,
        exportRunsheet,
        isCategorizationEnabled,
        
        // Methods
        fetchRunsheetById,
        handleExportRunsheetClick,
        handleUpdateDocuments,
        onDrop,
        removeDocumentsBulk,
        removeDocsFromTab,
        submitCopyRunsheet,
        submitCopyDocuments,
        
    }
    
}

export default RunsheetViewModel
